// src/components/BiodataDetailsView.jsx
import Icon from "components/AppIcon";
import Image from "components/AppImage";
import StatusIndicatorBadge from "components/ui/StatusIndicatorBadge";
import { useState } from "react";
import DocumentPreviewModal from "./DocumentPreviewModal";
import FilePreview, { FilePreviewModal } from "./FilePreview";
import { useNotifier } from '../context/NotificationContext';

// Reusable UI Components
const Label = ({ children }) => (
  <label className="text-xs font-semibold text-text-secondary uppercase tracking-wide">
    {children}
  </label>
);

const Field = ({ label, value, mono, icon, uppercase = true }) => (
  <div className="space-y-1.5">
    <Label>{label}</Label>
    <div className="flex items-center gap-2">
      {icon && <Icon name={icon} size={16} className="text-text-secondary" />}
      <p
        className={`text-text-primary text-sm ${
          mono ? "font-mono bg-secondary-50 px-2 py-1 rounded" : ""
        } ${uppercase && !mono ? "uppercase" : ""}`}
      >
        {value ?? "Not specified"}
      </p>
    </div>
  </div>
);

const Card = ({ children, className = "", hover = false }) => (
  <div
    className={`bg-surface border border-secondary-200 rounded-xl p-6 shadow-sm ${
      hover
        ? "transition-all duration-200 hover:shadow-md hover:border-primary-200"
        : ""
    } ${className}`}
  >
    {children}
  </div>
);

const SectionHeader = ({ icon, title, badge }) => (
  <div className="flex items-center justify-between mb-6">
    <div className="flex items-center gap-3">
      {icon && (
        <div className="w-10 h-10 rounded-lg bg-primary-100 flex items-center justify-center">
          <Icon name={icon} size={20} className="text-primary-600" />
        </div>
      )}
      <h3 className="text-lg font-semibold text-text-primary">{title}</h3>
    </div>
    {badge}
  </div>
);

const formatDate = (dateString) => {
  if (!dateString) return "Not specified";
  const d = new Date(dateString);
  if (isNaN(d.getTime())) return "Not specified";
  return d.toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
};

const calculateAge = (dateString) => {
  if (!dateString) return null;
  const birthDate = new Date(dateString);
  if (isNaN(birthDate.getTime())) return null;

  const today = new Date();
  let age = today.getFullYear() - birthDate.getFullYear();
  const monthDiff = today.getMonth() - birthDate.getMonth();

  if (
    monthDiff < 0 ||
    (monthDiff === 0 && today.getDate() < birthDate.getDate())
  ) {
    age--;
  }

  return age;
};

const formatDateWithAge = (dateString) => {
  const formattedDate = formatDate(dateString);
  const age = calculateAge(dateString);

  if (formattedDate === "Not specified" || age === null) {
    return formattedDate;
  }

  return `${formattedDate} (${age} years old)`;
};

const toUpperCase = (value) => {
  if (!value) return value;
  return typeof value === "string" ? value.toUpperCase() : value;
};

const formatDocumentType = (type) => {
  const labels = {
    passport_copy: "FCL Passport or Identity Card",
    next_of_kin_identification: "Next of Kin Identity Card",
    vaccine_certificate: "Skilled Certificate",
    medical_certificate: "Medical Certificate (FWCMS)",
    highest_qualification: "Highest Qualification",
  };
  return labels[type] || type.replace(/_/g, " ");
};

const BiodataDetailsView = ({ biodata, title = "Biodata Details" }) => {
  const notify = useNotifier();
  const [selectedFile, setSelectedFile] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [isDocumentModalOpen, setIsDocumentModalOpen] = useState(false);

  const photos = Array.isArray(biodata?.photos) ? biodata.photos : [];
  const docs = Array.isArray(biodata?.documents) ? biodata.documents : [];
  const nok = biodata?.next_of_kin || {};

  const resolveFileUrl = (file) => {
    if (!file) return null;
    const candidates = [
      file.file_url,
      file.signed_url,
      file.download_url,
      file.url,
      file.path,
      file.file_path,
    ];
    const raw = candidates.find(Boolean);
    if (!raw) return null;
    const isAbsolute = /^(?:[a-z][a-z\d+\-.]*:|\/\/)/i.test(raw);
    if (isAbsolute || raw.startsWith("data:") || raw.startsWith("blob:")) {
      return raw;
    }

    const trimmed = raw.startsWith("/") ? raw : `/${raw}`;
    if (typeof window !== "undefined") {
      const base =
        window.__API_BASE_URL__ ||
        window.REACT_APP_API_BASE_URL ||
        window.VITE_API_BASE_URL ||
        window.location.origin;
      return `${base.replace(/\/$/, "")}${trimmed}`;
    }
    return trimmed;
  };

  const handleFileClick = (file) => {
    setSelectedFile(file);
    setIsModalOpen(true);
  };

  const handleDocumentClick = (doc) => {
    setSelectedDocument(doc);
    setIsDocumentModalOpen(true);
  };

  const handlePrint = () => {

    try {
      // Open a new window with BiodataPrintView structure (same as ClabBiodataApproved)
      const printWindow = window.open(
        "",
        "_blank",
        "width=800,height=600,scrollbars=yes,resizable=yes"
      );

      if (!printWindow) {
        throw new Error(
          "Popup blocked - please allow popups for this site to print"
        );
      }

      // Create HTML that follows the BiodataPrintView structure and styling
      const yearsValue = (() => {
        const raw =
          biodata?.experience_years ||
          biodata?.work_experience_years ||
          biodata?.experience ||
          biodata?.construction_experience;
        if (raw === null || raw === undefined) return "";
        const s = String(raw).trim();
        if (!s) return "";
        const direct = Number(s);
        if (!Number.isNaN(direct) && direct >= 0) return `${direct} years`;
        const m =
          s.match(/(\d+(?:\.\d+)?)\s*(?:years?|yrs?)/i) ||
          s.match(/(\d+(?:\.\d+)?)/);
        if (m) {
          const y = parseFloat(m[1]);
          if (!Number.isNaN(y)) return `${y} years`;
        }
        return s.toLowerCase() === "years" ? "" : s.toUpperCase();
      })();

      const printContent = `
        <!DOCTYPE html>
        <html>
          <head>
            <title>Biodata - ${biodata?.full_name || "Worker"}</title>
            <meta charset="utf-8">
            <style>
              @media print {
                @page {
                  size: A4;
                  margin: 6mm 8mm 10mm;
                }
                body {
                  margin: 0;
                  padding: 0;
                }
              }
              
              body {
                font-family: Arial, sans-serif;
                font-size: 8.5pt;
                padding: 0;
                max-width: 190mm;
                margin: 0 auto;
              }
              
              table {
                width: 100%;
                border-collapse: collapse;
                font-size: 8.2pt;
                margin-bottom: 13px;
              }
              
              th, td {
                border: 1px solid #000;
                padding: 6px 9px;
                text-align: left;
                vertical-align: middle;
              }
              
              th {
                font-weight: bold;
                background-color: #fdfdfd;
                width: 30%;
              }
              
              .section-title {
                font-weight: bold;
                margin-bottom: 4px;
                font-size: 9.5pt;
              }
              
              .header {
                display: flex;
                justify-content: space-between;
                align-items: center;
                margin-bottom: 9px;
              }
              
              .header img {
                width: 100%;
                height: auto;
                max-width: 182px;
              }
              
              .title {
                text-align: center;
                font-weight: bold;
                font-size: 12.3pt;
                margin-bottom: 9px;
                padding-bottom: 12px;
              }
              
              .content {
                display: flex;
                gap: 11px;
              }
              
              .left-column {
                flex: 1;
              }
              
              .right-column {
                width: 150px;
              }
              
              .photos {
                width: 150px;
                display: flex;
                flex-direction: column;
                gap: 18px;
              }
              
              .photo-box {
                width: 150px;
                border: 1px solid #000;
                display: flex;
                align-items: center;
                justify-content: center;
                background-color: #f8f8f8;
              }
              
              .passport-photo {
                height: 190px;
              }
              
              .fullbody-photo {
                height: 400px;
              }
              
              .photo-box img {
                max-width: 100%;
                max-height: 100%;
                object-fit: cover;
              }
            </style>
          </head>
          <body>
            <div class="header">
              <div style="width: 182px;">
                <img src="/assets/clab_logo.png" alt="CLAB" onerror="this.style.display='none'">
              </div>
              <div style="width: 182px;">
                <img src="/assets/images/cidb-logo.png" alt="CIDB" onerror="this.style.display='none'">
              </div>
            </div>
            
            <div class="title">FOREIGN CONSTRUCTION LABOUR (FCL) BIODATA</div>
            
            <div class="content">
              <div class="left-column">
                <div class="section-title">1. FCL DETAILS</div>
                <table>
                  <tr><th>Name</th><td colspan="3">${
                    biodata?.full_name || ""
                  }</td></tr>
                  <tr><th>Passport No</th><td colspan="3">${
                    biodata?.passport_number || biodata?.passport_no || ""
                  }</td></tr>
                  <tr><th>Address</th><td colspan="3">${
                    biodata?.address || ""
                  }</td></tr>
                  <tr><th>E-Mail Address</th><td colspan="3">${
                    biodata?.email || ""
                  }</td></tr>
                  <tr><th>Age</th><td>${
                    biodata?.date_of_birth
                      ? new Date().getFullYear() -
                        new Date(biodata.date_of_birth).getFullYear()
                      : ""
                  }</td><th>Date Of Birth</th><td>${
        biodata?.date_of_birth
          ? new Date(biodata.date_of_birth).toLocaleDateString("en-GB")
          : ""
      }</td></tr>
                  <tr><th>Marital Status</th><td>${
                    biodata?.marital_status || ""
                  }</td><th>Nationality</th><td>${
        biodata?.nationality || ""
      }</td></tr>
                  <tr><th>Height</th><td>${
                    biodata?.height_cm || ""
                  }</td><th>Weight</th><td>${biodata?.weight_kg || ""}</td></tr>
                  <tr><th>Contact Number (Handphone)</th><td colspan="3">${
                    biodata?.contact_no || ""
                  }</td></tr>
                </table>
                
                <div class="section-title">2. NEXT OF KIN DETAILS</div>
                <table style="table-layout: fixed;">
                  <tr><th style="width: 43%;">Name</th><td style="width: 57%;">${
                    biodata?.next_of_kin?.name || ""
                  }</td></tr>
                  <tr><th>Relationship</th><td>${
                    biodata?.next_of_kin?.relationship || ""
                  }</td></tr>
                  <tr><th>Identification No</th><td>${
                    biodata?.next_of_kin?.identification_no || ""
                  }</td></tr>
                  <tr><th>Address</th><td>${
                    biodata?.next_of_kin?.address || ""
                  }</td></tr>
                  <tr><th>Contact Number</th><td>${
                    biodata?.next_of_kin?.contact_no || ""
                  }</td></tr>
                  <tr><th>Bank Account No</th><td>${
                    biodata?.next_of_kin?.bank_account_no || ""
                  }</td></tr>
                  <tr><th>Bank Name</th><td>${
                    biodata?.next_of_kin?.bank_name || ""
                  }</td></tr>
                </table>
                
                <div class="section-title">3. WORKING DETAILS</div>
                <table style="table-layout: fixed;">
                  <tr><th style="width: 43%;">Highest Qualification</th><td style="width: 57%;">${
                    biodata?.education_level || ""
                  }</td></tr>
                  <tr><th>Working Experience in Construction Industry</th><td>${yearsValue}</td></tr>
                  <tr><th>Skill</th><td>${
                    Array.isArray(biodata?.skills)
                      ? biodata.skills.join(", ")
                      : biodata?.skills || biodata?.skill || ""
                  }</td></tr>
                </table>

                <div class="section-title">4. RECRUITER DETAIL</div>
                <div style="font-size: 10pt; font-weight: bold; padding-left: 4px; margin-bottom: 20px;">
                  ${
                    biodata?.recruiter?.name ||
                    biodata?.recruiters?.agency_name ||
                    ""
                  }
                </div>

              </div>
              
              <div class="right-column">
                <div class="photos">
                  <div class="photo-box passport-photo">
                    <img src="${
                      biodata?.photos?.[0]?.file_url || biodata?.photo_url || ""
                    }" alt="Passport Photo" style="width: 100%; height: 100%; object-fit: contain;" onerror="this.style.display='none'; this.parentElement.innerHTML='<div style=text-align:center;color:#999;font-size:10pt>No Photo</div>'">
                  </div>
                  <div class="photo-box fullbody-photo">
                    <img src="${
                      biodata?.photos?.[1]?.file_url || ""
                    }" alt="Full Body Photo" style="width: 100%; height: 100%; object-fit: contain;" onerror="this.style.display='none'; this.parentElement.innerHTML='<div style=text-align:center;color:#999;font-size:10pt>No Photo</div>'">
                  </div>
                </div>
              </div>
            </div>
            
            <script>
              // Auto-print after content loads
              setTimeout(function() {
                window.print();
              }, 300);
            </script>
          </body>
        </html>
      `;

      printWindow.document.write(printContent);
      printWindow.document.close();

    } catch (error) {
      console.error("Print error in BiodataDetailsView:", error);

      // Fallback: open print view in new window using BiodataPrintView
      try {
        const printWindow = window.open(
          "",
          "_blank",
          "width=800,height=600,scrollbars=yes,resizable=yes"
        );
        if (!printWindow) {
          throw new Error(
            "Popup blocked - please allow popups for this site to print"
          );
        }

        // Create HTML that mimics BiodataPrintView styling
        const printContent = `
          <!DOCTYPE html>
          <html>
            <head>
              <title>Biodata - ${biodata?.full_name || "Worker"}</title>
              <meta charset="utf-8">
              <style>
                @media print {
                  @page {
                    size: A4;
                    margin: 6mm 8mm 10mm;
                  }
                }
                
                body {
                  font-family: Arial, sans-serif;
                  font-size: 8.5pt;
                  margin: 0;
                  padding: 0;
                  max-width: 190mm;
                  margin: 0 auto;
                }
                
                table {
                  width: 100%;
                  border-collapse: collapse;
                  font-size: 8.2pt;
                  margin-bottom: 13px;
                }
                
                th, td {
                  border: 1px solid #000;
                  padding: 6px 9px;
                  text-align: left;
                  vertical-align: middle;
                }
                
                th {
                  font-weight: bold;
                  background-color: #fdfdfd;
                  width: 30%;
                }
                
                .section-title {
                  font-weight: bold;
                  margin-bottom: 4px;
                  font-size: 9.5pt;
                }
                
                .header {
                  display: flex;
                  justify-content: space-between;
                  align-items: center;
                  margin-bottom: 9px;
                }
                
                .header img {
                  width: 100%;
                  height: auto;
                  max-width: 182px;
                }
                
                .title {
                  text-align: center;
                  font-weight: bold;
                  font-size: 12.3pt;
                  margin-bottom: 9px;
                  padding-bottom: 12px;
                }
                
                .photos {
                  width: 150px;
                  display: flex;
                  flex-direction: column;
                  gap: 18px;
                }
                
                .photo-box {
                  width: 150px;
                  border: 1px solid #000;
                  display: flex;
                  align-items: center;
                  justify-content: center;
                  background-color: #f8f8f8;
                }
                
                .passport-photo {
                  height: 190px;
                }
                
                .fullbody-photo {
                  height: 400px;
                }
                
                .photo-box img {
                  max-width: 100%;
                  max-height: 100%;
                  object-fit: cover;
                }
                
                .content {
                  display: flex;
                  gap: 11px;
                }
                
                .left-column {
                  flex: 1;
                }
                
                .right-column {
                  width: 150px;
                }
                
                .footer-notes {
                  font-size: 10pt;
                  padding-left: 4px;
                  margin-bottom: 20px;
                }
              </style>
            </head>
            <body>
              <div class="header">
                <div style="width: 182px;">
                  <img src="/assets/clab_logo.png" alt="CLAB" onerror="this.style.display='none'">
                </div>
                <div style="width: 182px;">
                  <img src="/assets/images/cidb-logo.png" alt="CIDB" onerror="this.style.display='none'">
                </div>
              </div>
              
              <div class="title">FOREIGN CONSTRUCTION LABOUR (FCL) BIODATA</div>
              
              <div class="content">
                <div class="left-column">
                  <div class="section-title">1. FCL DETAILS</div>
                  <table>
                    <tr><th>Name</th><td colspan="3">${
                      biodata?.full_name || biodata?.fullName || ""
                    }</td></tr>
                    <tr><th>Passport No</th><td colspan="3">${
                      biodata?.passport_number ||
                      biodata?.passport_no ||
                      biodata?.passportNo ||
                      ""
                    }</td></tr>
                    <tr><th>Address</th><td colspan="3">${
                      biodata?.address || ""
                    }</td></tr>
                    <tr><th>E-Mail Address</th><td colspan="3">${
                      biodata?.email || ""
                    }</td></tr>
                    <tr><th>Age</th><td>${
                      biodata?.date_of_birth
                        ? new Date().getFullYear() -
                          new Date(biodata.date_of_birth).getFullYear()
                        : ""
                    }</td><th>Date Of Birth</th><td>${
          biodata?.date_of_birth
            ? new Date(biodata.date_of_birth).toLocaleDateString("en-GB")
            : ""
        }</td></tr>
                    <tr><th>Marital Status</th><td>${
                      biodata?.marital_status || biodata?.maritalStatus || ""
                    }</td><th>Nationality</th><td>${
          biodata?.nationality || ""
        }</td></tr>
                    <tr><th>Height</th><td>${
                      biodata?.height_cm || biodata?.heightCm || ""
                    }</td><th>Weight</th><td>${
          biodata?.weight_kg || biodata?.weightKg || ""
        }</td></tr>
                    <tr><th>Contact Number (Handphone)</th><td colspan="3">${
                      biodata?.contact_no ||
                      biodata?.contactNo ||
                      biodata?.phone ||
                      ""
                    }</td></tr>
                  </table>
                  
                  <div class="section-title">2. NEXT OF KIN DETAILS</div>
                  <table style="table-layout: fixed;">
                    <tr><th style="width: 43%;">Name</th><td style="width: 57%;">${
                      biodata?.next_of_kin?.name ||
                      biodata?.nextOfKin?.name ||
                      ""
                    }</td></tr>
                    <tr><th>Relationship</th><td>${
                      biodata?.next_of_kin?.relationship ||
                      biodata?.nextOfKin?.relationship ||
                      ""
                    }</td></tr>
                    <tr><th>Identification No</th><td>${
                      biodata?.next_of_kin?.identification_no ||
                      biodata?.nextOfKin?.identificationNo ||
                      ""
                    }</td></tr>
                    <tr><th>Address</th><td>${
                      biodata?.next_of_kin?.address ||
                      biodata?.nextOfKin?.address ||
                      ""
                    }</td></tr>
                    <tr><th>Contact Number</th><td>${
                      biodata?.next_of_kin?.contact_no ||
                      biodata?.nextOfKin?.contactNo ||
                      ""
                    }</td></tr>
                    <tr><th>Bank Account No</th><td>${
                      biodata?.next_of_kin?.bank_account_no ||
                      biodata?.nextOfKin?.bankAccountNo ||
                      ""
                    }</td></tr>
                    <tr><th>Bank Name</th><td>${
                      biodata?.next_of_kin?.bank_name ||
                      biodata?.nextOfKin?.bankName ||
                      ""
                    }</td></tr>
                  </table>
                  
                  <div class="section-title">3. WORKING DETAILS</div>
                  <table style="table-layout: fixed;">
                    <tr><th style="width: 43%;">Highest Qualification</th><td style="width: 57%;">${
                      biodata?.education_level || biodata?.educationLevel || ""
                    }</td></tr>
                    <tr><th>Working Experience in Construction Industry</th><td>${
                      biodata?.experience ||
                      biodata?.construction_experience ||
                      ""
                    }</td></tr>
                    <tr><th>Skill</th><td>${
                      biodata?.skills || biodata?.skill || ""
                    }</td></tr>
                  </table>
                  
                  <div class="section-title">4. RECRUITER DETAIL</div>
                  <div style="font-size: 10pt; font-weight: bold; padding-left: 4px; margin-bottom: 20px;">${
                    biodata?.recruiter?.name ||
                    biodata?.recruiters?.agency_name ||
                    ""
                  }</div>
                  
                  <div class="footer-notes">
                    <div style="font-weight: bold; margin-bottom: 5px;">Please attach:</div>
                    <div>1. Copy of FCL Passport</div>
                    <div>2. Copy of Next of Kin Identification Card</div>
                  </div>
                </div>
                
                <div class="right-column">
                  <div class="photo-box passport-photo">
                    <img src="${
                      biodata?.photos?.[0]?.file_url || ""
                    }" alt="Passport Photo" style="width: 100%; height: 100%; object-fit: cover;" onerror="this.style.display='none'; this.parentElement.innerHTML='<div style=text-align:center;color:#999;font-size:10pt>No Photo</div>'">
                  </div>
                  <div class="photo-box fullbody-photo">
                    <img src="${
                      biodata?.photos?.[1]?.file_url || ""
                    }" alt="Full Body Photo" style="width: 100%; height: 100%; object-fit: contain;" onerror="this.style.display='none'; this.parentElement.innerHTML='<div style=text-align:center;color:#999;font-size:10pt>No Photo</div>'">
                  </div>
                </div>
              </div>
              
              <script>
                // Auto-print after content loads
                setTimeout(function() {
                  window.print();
                }, 300);
              </script>
            </body>
          </html>
        `;

        printWindow.document.write(printContent);
        printWindow.document.close();

      } catch (fallbackError) {
        console.error("Fallback print also failed:", fallbackError);
        notify.error(
          "Unable to print. Please check your browser settings and ensure popups are allowed."
        );
      }
    }
  };

  return (
    <div className="space-y-6 max-w-7xl mx-auto">
      {/* Header with gradient background */}
      <div className="bg-gradient-to-r from-primary-50 to-accent-50 rounded-2xl p-6 border border-primary-100">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-2xl font-bold text-text-primary">{title}</h2>
          <div className="flex items-center gap-3">
            <button
              onClick={handlePrint}
              className="flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-700 transition-colors duration-150 print:hidden"
            >
              <Icon name="Printer" size={18} />
              <span className="font-medium">Print</span>
            </button>
            <StatusIndicatorBadge status={biodata?.status} size="md" />
          </div>
        </div>

        {/* Profile Header */}
        <div className="flex items-start gap-6">
          <div className="relative">
            <div className="w-24 h-24 rounded-2xl overflow-hidden bg-gradient-to-br from-primary-100 to-accent-100 ring-4 ring-white shadow-lg">
              {photos.length > 0 ? (
                <Image
                  src={photos[0].file_url || photos[0].file_path}
                  alt={biodata.full_name}
                  className="w-full h-full object-cover"
                />
              ) : biodata.photo_url ? (
                <Image
                  src={biodata.photo_url}
                  alt={biodata.full_name}
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <Icon name="User" size={40} className="text-primary-400" />
                </div>
              )}
            </div>
            {biodata?.status === "approved" && (
              <div className="absolute -bottom-2 -right-2 w-8 h-8 bg-success rounded-full flex items-center justify-center ring-4 ring-white shadow-md">
                <Icon name="CheckCircle" size={18} className="text-white" />
              </div>
            )}
          </div>

          <div className="flex-1">
            <div className="flex items-start justify-between">
              <div>
                <h3 className="text-2xl font-bold text-text-primary mb-1 uppercase">
                  {biodata.full_name}
                </h3>
                <div className="flex items-center gap-3 text-text-secondary mb-3">
                  <div className="flex items-center gap-1.5">
                    <Icon name="MapPin" size={16} />
                    <span>{biodata.nationality || "Unknown"}</span>
                  </div>
                  {biodata?.worker_id && (
                    <>
                      <span className="text-secondary-300">•</span>
                      <div className="flex items-center gap-1.5">
                        <Icon name="Hash" size={16} />
                        <span className="font-mono text-sm">
                          {biodata.worker_id}
                        </span>
                      </div>
                    </>
                  )}
                </div>
                {biodata.vdr_type && (
                  <div className="inline-flex items-center px-4 py-2 bg-white rounded-lg shadow-sm border border-accent-200 text-accent-800 text-sm font-semibold">
                    <Icon name="Briefcase" size={16} className="mr-2" />
                    {biodata.vdr_type}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Personal Information Card */}
      <Card>
        <SectionHeader icon="User" title="Personal Information" />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <Field
            label="Worker ID"
            value={biodata.worker_id || biodata.id || "Not assigned"}
            icon="Hash"
            mono
          />
          <Field
            label="Date of Birth"
            value={formatDateWithAge(
              biodata.date_of_birth || biodata.dateOfBirth
            )}
            icon="Calendar"
            uppercase={false}
          />
          <Field label="Gender" value={biodata.gender} icon="Users" />
          <Field label="Nationality" value={biodata.nationality} icon="Globe" />
          <Field
            label="Education Level"
            value={biodata.education_level || biodata.educationLevel}
            icon="GraduationCap"
          />
          <Field label="Religion" value={biodata.religion} icon="Book" />
        </div>
      </Card>

      {/* Documentation & Medical Card */}
      <Card>
        <SectionHeader icon="FileText" title="Documentation & Medical" />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <Field
            label="Passport Number"
            value={biodata.passport_number || biodata.passport_no}
            icon="CreditCard"
            mono
          />
          <Field
            label="Passport Place of Issue"
            value={
              biodata.passport_place_of_issue || biodata.passportPlaceOfIssue
            }
            icon="MapPin"
          />
          <Field
            label="Passport Expiry Date"
            value={formatDate(
              biodata.passport_expiry_date || biodata.passportExpiryDate
            )}
            icon="Calendar"
            uppercase={false}
          />
          <Field
            label="Marital Status"
            value={biodata.marital_status || biodata.maritalStatus}
            icon="Heart"
          />
          <Field
            label="Medical Status"
            value={biodata.medical_status}
            icon="Activity"
          />
          <Field
            label="Medical Expiry"
            value={formatDate(biodata.medical_expiry_date)}
            icon="Calendar"
            uppercase={false}
          />
          <Field
            label="Height"
            value={biodata.height_cm ? `${biodata.height_cm} cm` : undefined}
            icon="Maximize2"
          />
          <Field
            label="Weight"
            value={biodata.weight_kg ? `${biodata.weight_kg} kg` : undefined}
            icon="Minimize2"
          />
        </div>
      </Card>

      {/* Skills & Experience Section */}
      <Card>
        <SectionHeader icon="Award" title="Skills & Experience" />
        <div className="space-y-6">
          {/* Skills */}
          {biodata.skill && (
            <div>
              <Label>Skills</Label>
              <div className="mt-2">
                {typeof biodata.skill === "string" ? (
                  <div className="flex flex-wrap gap-2">
                    {biodata.skill.split(",").map((skill, index) => (
                      <span
                        key={index}
                        className="px-3 py-1.5 bg-primary-50 text-primary-700 rounded-lg text-sm font-medium border border-primary-200"
                      >
                        {skill.trim()}
                      </span>
                    ))}
                  </div>
                ) : Array.isArray(biodata.skill) ? (
                  <div className="flex flex-wrap gap-2">
                    {biodata.skill.map((skill, index) => (
                      <span
                        key={index}
                        className="px-3 py-1.5 bg-primary-50 text-primary-700 rounded-lg text-sm font-medium border border-primary-200"
                      >
                        {skill}
                      </span>
                    ))}
                  </div>
                ) : (
                  <p className="text-text-primary text-sm">{biodata.skill}</p>
                )}
              </div>
            </div>
          )}

          {/* Experience */}
          {biodata.experience && (
            <div>
              <Label>Work Experience</Label>
              <div className="mt-2 p-4 bg-secondary-50 rounded-lg border border-secondary-200">
                <p className="text-text-primary text-sm whitespace-pre-wrap">
                  {biodata.experience}
                </p>
              </div>
            </div>
          )}

          {!biodata.skill && !biodata.experience && (
            <div className="flex flex-col items-center justify-center py-8 text-center">
              <div className="w-16 h-16 rounded-full bg-secondary-100 flex items-center justify-center mb-4">
                <Icon name="Award" size={32} className="text-secondary-400" />
              </div>
              <p className="text-text-secondary">
                No skills or experience information available
              </p>
            </div>
          )}
        </div>
      </Card>

      {/* Contact & Next of Kin Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <SectionHeader icon="Mail" title="Contact Information" />
          <div className="space-y-4">
            <Field label="Email" value={biodata.email} icon="Mail" />
            <Field
              label="Contact Number"
              value={biodata.contact_no}
              icon="Phone"
            />
            <Field label="Address" value={biodata.address} icon="MapPin" />
          </div>
        </Card>

        <Card>
          <SectionHeader icon="Users" title="Next of Kin" />
          <div className="space-y-4">
            <Field label="Name" value={nok.name} icon="User" />
            <Field label="Relationship" value={nok.relationship} icon="Heart" />
            <Field label="Contact Number" value={nok.contact_no} icon="Phone" />
            <Field
              label="Identification No"
              value={nok.identification_no}
              icon="CreditCard"
              mono
            />
            <Field label="Address" value={nok.address} icon="MapPin" />
          </div>
        </Card>
      </div>

      {/* Documents Section */}
      <Card>
        <SectionHeader
          icon="FileText"
          title="Documents"
          badge={
            docs.length > 0 && (
              <span className="px-3 py-1 bg-primary-100 text-primary-700 rounded-full text-xs font-semibold">
                {docs.length} {docs.length === 1 ? "document" : "documents"}
              </span>
            )
          }
        />
        {docs.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <div className="w-16 h-16 rounded-full bg-secondary-100 flex items-center justify-center mb-4">
              <Icon name="FileText" size={32} className="text-secondary-400" />
            </div>
            <p className="text-text-secondary">No documents uploaded yet</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {docs.map((doc) => {
              return (
                <div
                  key={doc.id}
                  className="group border border-secondary-200 rounded-xl p-4 hover:border-primary-300 hover:shadow-md transition-all duration-200"
                >
                  <div className="flex items-center gap-4">
                    <div
                      className="w-14 h-14 rounded-lg overflow-hidden bg-secondary-50 flex-shrink-0 cursor-pointer"
                      onClick={() => handleDocumentClick(doc)}
                    >
                      <FilePreview
                        file={doc}
                        className="w-full h-full"
                        showFileName={false}
                        onClick={() => handleDocumentClick(doc)}
                      />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-text-primary text-sm mb-1 truncate">
                        {formatDocumentType(doc.document_type)}
                      </div>
                      <div className="text-xs text-text-secondary truncate">
                        {doc.file_name}
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => handleDocumentClick(doc)}
                      className="flex items-center gap-2 px-3 py-2 text-primary bg-primary-50 rounded-lg hover:bg-primary-100 text-sm font-medium transition-colors duration-150 flex-shrink-0"
                    >
                      <Icon name="Eye" size={14} />
                      <span className="hidden sm:inline">View</span>
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </Card>

      {/* Photos Gallery */}
      <Card>
        <SectionHeader
          icon="Image"
          title="Photos"
          badge={
            photos.length > 0 && (
              <span className="px-3 py-1 bg-accent-100 text-accent-700 rounded-full text-xs font-semibold">
                {photos.length} {photos.length === 1 ? "photo" : "photos"}
              </span>
            )
          }
        />
        {photos.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <div className="w-16 h-16 rounded-full bg-secondary-100 flex items-center justify-center mb-4">
              <Icon name="Image" size={32} className="text-secondary-400" />
            </div>
            <p className="text-text-secondary">No photos uploaded yet</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
            {photos.map((p) => (
              <div
                key={p.id}
                className="group relative border border-secondary-200 rounded-xl overflow-hidden hover:border-primary-300 hover:shadow-lg transition-all duration-200 cursor-pointer"
                onClick={() => handleFileClick(p)}
              >
                <div className="aspect-square bg-secondary-50">
                  <FilePreview
                    file={p}
                    className="w-full h-full"
                    showFileName={false}
                  />
                </div>
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                  <div className="absolute bottom-0 left-0 right-0 p-3">
                    <div className="text-white text-xs font-medium capitalize flex items-center gap-1">
                      <Icon name="Image" size={12} />
                      {p.photo_type}
                    </div>
                  </div>
                </div>
                <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                  <div className="w-8 h-8 bg-white rounded-full flex items-center justify-center shadow-lg">
                    <Icon name="ZoomIn" size={16} className="text-primary" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* File Preview Modal */}
      <FilePreviewModal
        file={selectedFile}
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
      />

      {/* Document Preview Modal */}
      <DocumentPreviewModal
        file={selectedDocument}
        isOpen={isDocumentModalOpen}
        onClose={() => setIsDocumentModalOpen(false)}
        title={
          selectedDocument
            ? formatDocumentType(selectedDocument.document_type)
            : ""
        }
      />
    </div>
  );
};

export default BiodataDetailsView;
